import { getError } from '../utils/errorUtils';

export type HttpMethod = 'get' | 'post' | 'put' | 'delete';

export type ApiRequestOptions<TRequestBody = undefined> = {
  httpMethod: HttpMethod;
  endpointUrl: string;
  accessToken?: string;
} & (TRequestBody extends undefined
  ? {
      requestBody?: undefined;
    }
  : {
      requestBody: TRequestBody;
    });

export type ApiError = {
  message: string;
  stackTrace: string | null;
  userVisibleMessage: string | null;
};

export type ApiRequestResult<TResponseBody> =
  | { success: true; body: TResponseBody; error?: undefined; headers: Headers }
  | { success: false; body?: undefined; error: ApiError; headers: Headers | null };

export const makeRequest = async <TResponseBody, TRequestBody = undefined>(
  requestOptions: ApiRequestOptions<TRequestBody>,
  abortSignal?: AbortSignal,
): Promise<ApiRequestResult<TResponseBody>> => {
  const options = {
    method: requestOptions.httpMethod,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
    body: requestOptions.requestBody == null ? undefined : JSON.stringify(requestOptions.requestBody),
    signal: abortSignal,
  };

  if (requestOptions.accessToken) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (options.headers as any)['Authorization'] = `Bearer ${requestOptions.accessToken}`;
  }

  try {
    const response = await fetch(`/api/${requestOptions.endpointUrl}`, options);
    const contentTypes = response.headers.get('Content-Type');
    const isJsonResponse = contentTypes != null && contentTypes.indexOf('application/json') !== -1;
    const isPdfResponse = contentTypes != null && contentTypes.indexOf('application/pdf') !== -1;

    if (response.ok) {
      const responseBody: TResponseBody = isJsonResponse
        ? await response.json()
        : isPdfResponse
        ? await response.arrayBuffer()
        : await response.text();
      return {
        success: true,
        body: responseBody,
        headers: response.headers,
      };
    } else {
      const apiError: ApiError = isJsonResponse
        ? await response.json()
        : {
            message: (await response.text()) || `Error: ${response.status} (${response.statusText})`,
            stackTrace: null,
            userVisibleMessage: null,
          };
      return { success: false, error: apiError, headers: response.headers };
    }
  } catch (unknownError) {
    const error = getError(unknownError);
    let errorMessage = error.message;

    if (
      error.name === 'AbortError' ||
      error.message === 'The operation was aborted. ' ||
      error.message === 'The user aborted a request.'
    ) {
      errorMessage = abortedRequestErrorMessage;
    }

    return {
      success: false,
      error: {
        message: errorMessage,
        stackTrace: null,
        userVisibleMessage: null,
      },
      headers: null,
    };
  }
};

const abortedRequestErrorMessage = 'Request Aborted';

export const isRequestAbortedErrorMessage = (errorMessage: string) => errorMessage === abortedRequestErrorMessage;
